'use client'

import { type ChangeEvent, type HTMLAttributes, useContext, useEffect, useState } from 'react'

import cn from 'clsx'
import { HelpCenterContext } from '@/components/HtmlArticle/HelpCenterContext'
import { Heading } from '@/components/Text/Heading'

import type { Themes } from '@/Theme'

import css from './help-center-feedback.module.css'
import { Box } from '@/components/Box/Box'
import { Button } from '@/components/Button/Button'
import { Image } from '@/components/image/image'
import { Text } from '@/components/Text/Text'

export enum HelpCenterFeedbackSteps {
    Login = 'login',
    Choices = 'choices',
    Feedback = 'feedback',
    Success = 'success',
}
export type HelpCenterFeedbackProps = HTMLAttributes<HTMLElement> & {
    theme: Themes
    initialFlowState: HelpCenterFeedbackSteps
    onLogin?: () => void
    onPositiveFeedback?: () => void
    onNegativeFeedback?: (reason: string) => void
    className?: string
    illustration?: {
        src: string
        width: number
        height: number
    }
}

/** Give a feedback to a Help Center article. */
export function HelpCenterFeedback({
    theme = 'todoist',
    initialFlowState,
    onLogin,
    onPositiveFeedback,
    onNegativeFeedback,
    className,
    illustration,
    ...rest
}: HelpCenterFeedbackProps) {
    const { labels } = useContext(HelpCenterContext)
    const [negativeText, setNegativeText] = useState('')
    const [flowState, setFlowState] = useState(initialFlowState)
    const [isAnimating, setAnimating] = useState(false)

    function getTitle(step: HelpCenterFeedbackSteps) {
        if (['login', 'choices'].includes(step)) {
            return labels.help.feedbacks.title
        }
        if (step === 'feedback') {
            return labels.help.feedbacks.improveTitle
        }
        /** step === 'success' */
        return labels.help.feedbacks.thanks
    }

    /** React when the `initialFlowState props is getting updated */
    useEffect(() => {
        setFlowState(initialFlowState)
    }, [initialFlowState])

    return (
        <Box className={cn(css.feedbackForm, css[theme], className)} {...rest}>
            {illustration ? (
                <Image
                    className={css.illus}
                    src={illustration.src}
                    width={illustration.width}
                    height={illustration.height}
                    alt=""
                />
            ) : null}

            <Heading level="4" size="major-base">
                {getTitle(flowState)}
            </Heading>

            {flowState === 'login' && (
                <>
                    <Text>{labels.help.feedbacks.textLogin}</Text>
                    <Box className={css.feelingBtns}>
                        <Button variant="secondary" onClick={onLogin}>
                            {labels.help.feedbacks.login}
                        </Button>
                    </Box>
                </>
            )}

            {flowState === 'choices' && (
                <Box>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setFlowState(HelpCenterFeedbackSteps.Success)
                            onPositiveFeedback?.()
                        }}
                    >
                        {labels.help.feedbacks.yes}
                    </Button>

                    <Button
                        variant="secondary"
                        onClick={() => setFlowState(HelpCenterFeedbackSteps.Feedback)}
                    >
                        {labels.help.feedbacks.no}
                    </Button>
                </Box>
            )}

            {flowState === 'feedback' && (
                <Box className={css.feedback}>
                    <Text
                        tag="textarea"
                        value={negativeText}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            setNegativeText(e.target.value)
                        }
                        size="body-sm"
                        placeholder={labels.help.feedbacks.improvePlaceholder}
                        autoFocus
                    />
                    <Box className={css.submitBtns}>
                        <Button
                            onClick={() => setFlowState(HelpCenterFeedbackSteps.Choices)}
                            variant="secondary"
                        >
                            {labels.help.feedbacks.cancel}
                        </Button>

                        <Button
                            variant="primary"
                            onClick={() => {
                                setAnimating(true)
                                onNegativeFeedback?.(negativeText)
                                setTimeout(() => {
                                    setAnimating(false)
                                    setFlowState(HelpCenterFeedbackSteps.Success)
                                }, 500)
                            }}
                            disabled={isAnimating}
                        >
                            {labels.help.feedbacks.send}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
